import type { CronExecutionOut } from '@/api/endpoints/scheduler'
import { formatWhen } from '@/features/scheduler/channelCards'

/**
 * O que sobrou do painel "Saúde do scheduler": um aviso só quando alguma
 * rotina está com erro ou parada. Sem ele, um scheduler morto deixaria todos
 * os cards verdes, porque a próxima geração do card vem do relógio.
 */

const LABELS: Readonly<Record<string, string>> = {
  generation_cycle_scheduler: 'Ciclo de geração',
  upload_cycle_scheduler: 'Ciclo de upload',
  upload_scheduler: 'Upload agendado',
  engagement_cycle: 'Interações',
  destination_publisher: 'Publicador de destinos',
  destination_repair: 'Reparo de destinos',
  channel_health: 'Saúde dos canais',
  cleanup_cycle_scheduler: 'Limpeza do backlog',
  platform_stats: 'Estatísticas das plataformas',
  ranking_cycle: 'Ciclo diário de ranking',
  instagram_token_refresh: 'Renovação do token do Instagram',
  job_worker: 'Worker da fila',
  // scripts/db-backup.sh, no cron do host do skab (fora do backend).
  db_backup: 'Backup do banco',
}

// Uma rotina parou quando passou do próximo tick esperado com folga e não está
// rodando (um ciclo longo, como o cortes, fica em ``running`` e não conta).
//
// - O ``next_run_at`` é o início da volta mais o intervalo. Depois de um ciclo
//   de horas ele já nasce vencido, e o tick seguinte só vem um intervalo depois
//   do fim: o esperado é o mais tarde entre ele e ``fim + intervalo``.
// - A folga cresce com o intervalo: uma rotina diária (``channel_health``, que
//   só roda numa janela de 5 min) que perde uma janela por um deploy não está
//   parada; perder duas, está.
const MIN_GRACE_MS = 15 * 60_000

function staleSince(c: CronExecutionOut, now: Date): boolean {
  const due = c.next_run_at ? Date.parse(c.next_run_at) : Number.NaN
  if (Number.isNaN(due)) return false
  const started = c.last_started_at ? Date.parse(c.last_started_at) : Number.NaN
  const finished = c.last_finished_at ? Date.parse(c.last_finished_at) : Number.NaN
  const interval = !Number.isNaN(started) && due > started ? due - started : 0
  const expected = Number.isNaN(finished) ? due : Math.max(due, finished + interval)
  return now.getTime() - expected > Math.max(MIN_GRACE_MS, interval)
}

export interface CronAlert {
  label: string
  detail: string
}

export function cronAlerts(items: readonly CronExecutionOut[], now: Date): CronAlert[] {
  const out: CronAlert[] = []
  for (const c of items) {
    const label = LABELS[c.cron_name] ?? c.cron_name
    if (c.last_status === 'error') {
      // O texto do erro só vem para o admin; os outros veem só que falhou.
      out.push({ label, detail: c.last_error || 'com erro' })
      continue
    }
    if (c.last_status !== 'running' && staleSince(c, now)) {
      out.push({ label, detail: `parado, sem rodar desde ${formatWhen(c.last_finished_at ?? c.last_started_at, now)}` })
    }
  }
  return out
}
